import { type FieldValues, FormProvider, useForm } from "react-hook-form";
import { api } from "@lib/client";
import type { Ticket } from "@lib/ticket";
import { useCallback, useEffect, useState } from "react";
import { VerificationModal } from "./verification-modal";
import { type Values } from "@lib/utils";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  extractUploadBlock,
  processUgc,
  deleteUgc,
} from "@lib/image-upload.client";
import { DeleteImageProvider } from "@/lib/context";
import type {
  GenericLandingPage,
  ProductSelectionBlockV2,
} from "@lib/config/types";
import { metaEvent } from "@/components/tracking/meta-pixel";
import { sendSubmitOrderGTMEvent } from "@/components/tracking/gtm-pixel";
import type { Activity } from "@lib/activity";
import { getAllocationIds } from "@lib/products";
import { FormStateContext } from "./form-state-context";
import { getHasShade } from "@/components/blocks/product-select-v2/shade-selector";
import { useSetAtom } from "jotai";
import { hashedEmailAtom } from "@/lib/state";

type LandingFormProps = {
  encodedActivityId: string;
  ticket: Ticket | undefined;
  children: React.ReactNode;
  config: GenericLandingPage;
  activity: Activity;
};

// INLINE_VALIDATION_OF_ADDRESSES_TEMP_CODE_START --
function getAddressSearchTestGroup(): "none" | "test" | "control" {
  switch (window.addressSearchTestActive) {
    case true:
      return "test";
    case false:
      return "control";
    case null:
      return "none";
  }
}
// INLINE_VALIDATION_OF_ADDRESSES_TEMP_CODE_END --

export function trackInPixel(
  activity: Activity,
  formValues: Values,
  orderId: string,
) {
  const productsAllocationIds = getAllocationIds(activity.products);

  const formAllocationIds = formValues
    ? ((formValues["order_allocation_ids"] || "") as string).split(",")
    : [""];

  const selectedAllocationIds =
    productsAllocationIds.type === "single"
      ? productsAllocationIds.allocationIds
      : formAllocationIds;

  sendSubmitOrderGTMEvent({
    activity: activity,
    orderId: orderId,
    selectedAllocationIds: selectedAllocationIds,
  });

  metaEvent("track", "SubmitApplication");
}

export function LandingForm({
  encodedActivityId,
  ticket,
  children,
  config,
  activity,
}: LandingFormProps) {
  const [disableSubmit, setDisableSubmit] = useState(false);
  const methods = useForm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState<FieldValues | null>(null);
  const navigate = useNavigate();
  const uploadBlock = extractUploadBlock(config.pages.landing);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const setHashedEmail = useSetAtom(hashedEmailAtom);

  const formValues = methods.getValues();
  const validationErrorsWithoutControl = Object.entries(
    validationErrors,
  ).filter(([key]) => !(key in formValues));

  useEffect(() => {
    Object.entries(validationErrors).forEach(([key, value]) => {
      methods.setError(key, { message: value });
    });
  }, [validationErrors]);

  const deleteImage = useCallback(
    (fileName: string) => deleteUgc(encodedActivityId, "claim", fileName),
    [encodedActivityId],
  );

  // INLINE_VALIDATION_OF_ADDRESSES_TEMP_CODE_START --
  const addressSearchTestGroup = getAddressSearchTestGroup();
  // INLINE_VALIDATION_OF_ADDRESSES_TEMP_CODE_END --

  const productSelectBlock = config.pages.landing.find(
    (block) => Object.keys(block)[0] === "product_selection_v2",
  ) as { product_selection_v2: ProductSelectionBlockV2 } | undefined;

  const productSelectLayout =
    productSelectBlock?.product_selection_v2.product_selection.layout;

  const { mutateAsync } = useMutation({
    mutationFn: async (formData: FieldValues) => {
      const ugc = formData["ugc"] as File[] | undefined;

      const { ugcMeta, processedImages } = await processUgc({
        methods,
        uploadBlock,
        encodedActivityId,
        flow: "claim",
        ugc,
      });

      if (processedImages) {
        formData["custom-question:ugc"] = processedImages;
      }

      // INLINE_VALIDATION_OF_ADDRESSES_TEMP_CODE_START --
      const res = await api.validate.$post({
        json: {
          encodedActivityId,
          isShadeSelector: productSelectLayout
            ? getHasShade(productSelectLayout)
            : false,
          // INLINE_VALIDATION_OF_ADDRESSES_TEMP_CODE_START --
          // ticket,
          ticket:
            addressSearchTestGroup === "none"
              ? ticket
              : {
                  ...ticket,
                  meta: {
                    ...ticket?.meta,
                    addressSearchTestGroup,
                  },
                },
          // INLINE_VALIDATION_OF_ADDRESSES_TEMP_CODE_END --
          formData: {
            ...(formData as Values),
            ugc: ugcMeta,
          },
        },
      });

      return res.json();
    },
    onSuccess: (data) => {
      if (!data.success && "errors" in data) {
        setValidationErrors(data.errors);
        return;
      }

      if ("redirect" in data) {
        if ("orderId" in data) {
          const orderId = data.orderId;
          // We only want to send a Pixel submitOrder event if an order has been created successfully
          trackInPixel(activity, formState as Values, orderId);
        }
        if ("hashedEmail" in data) {
          setHashedEmail(data.hashedEmail);
        }

        void navigate(data.redirect);
        return;
      }

      if (data.success && "values" in data) {
        setFormState(data.values);
        setDialogOpen(true);
      }
    },
  });

  async function onSubmit(formData: FieldValues) {
    await mutateAsync(formData);
  }

  return (
    <main>
      <DeleteImageProvider value={deleteImage}>
        <FormStateContext.Provider
          value={{
            validationErrorsWithoutControl,
            disableSubmit,
            setDisableSubmit,
          }}
        >
          <FormProvider {...methods}>
            <form
              onSubmit={(e) => {
                methods.clearErrors();
                void methods.handleSubmit(onSubmit)(e);
              }}
            >
              {children}
            </form>
          </FormProvider>
        </FormStateContext.Provider>
      </DeleteImageProvider>
      <VerificationModal
        encodedActivityId={encodedActivityId}
        isDialogOpen={Boolean(formState) && dialogOpen}
        onDialogOpenChange={(open) => setDialogOpen(open)}
        formValues={formState}
        // INLINE_VALIDATION_OF_ADDRESSES_TEMP_CODE_START --
        // ticket={ticket}
        ticket={
          addressSearchTestGroup === "none"
            ? ticket
            : {
                ...ticket,
                meta: {
                  ...ticket?.meta,
                  addressSearchTestGroup,
                },
              }
        }
        // INLINE_VALIDATION_OF_ADDRESSES_TEMP_CODE_END --
        activity={activity}
      />
    </main>
  );
}
